import { useMemo, useState, type ReactNode } from 'react';
import { Icon } from '../../components/Icon';
import { Avatars, Chip, Ring } from '../../components/bits';
import { Confirm } from '../../components/Shell';
import { useStore, tint, SERVICE_LABEL } from '../../state/store';
import { freeGaps, freeHours, occupancy, buildReschedule, findSlots } from '../../lib/scheduling';
import { dayVisits, progressOf, todayCounts } from '../../lib/derive';
import { addDays, dayLabel, fmtDuration, fmtHour } from '../../lib/time';
import { MONTH_TOTAL } from '../../data/mock';
import type { RescheduleRequest, ServiceType, Visit } from '../../data/types';
import type { Snapshot } from '../../lib/scheduling';
import { useModals } from '../../../../context/ModalContext';

const DAY_START = 8;
const DAY_END = 18;
const X = (h: number) => ((h - DAY_START) / (DAY_END - DAY_START)) * 100;
const HOURS = ['8', '9', '10', '11', '12', '1', '2', '3', '4', '5'];

function uniqueVisits(vs: Visit[]): Visit[] {
  const seen = new Set<string>();
  return vs.filter(v => { const k = v.uid.split(':')[0]!; if (seen.has(k)) return false; seen.add(k); return true; });
}

const EMPTY: Snapshot = { today: '', now: 0, teams: [], visits: [], blocks: [], clients: [], closedDays: [] };

/** Color de una visita: en datos reales, siempre por trabajador. */
function useColorOf(snap: Snapshot, isReal: boolean) {
  const s = useStore();
  return (v: Visit) => (isReal
    ? s.teamColors[v.teamId] ?? snap.teams.find(t => t.id === v.teamId)?.color ?? '#8A8B8E'
    : s.colorFor(v));
}

export function CalendarView() {
  const s = useStore();
  const isReal = s.dataSource === 'real';
  const realDay = s.real.day;
  const snap = isReal ? (realDay?.snapshot ?? EMPTY) : s.snapshot;
  const colorOf = useColorOf(snap, isReal);
  const [picked, setDate] = useState<string | null>(null);
  const date = picked ?? snap.today;
  const [selected, setSelected] = useState<string | null>(null);
  const [resched, setResched] = useState<RescheduleRequest | null>(null);
  // En datos reales una visita con 2 personas ocupa 2 carriles: se cuenta una sola vez.
  const counts = todayCounts(isReal ? { ...snap, today: date, visits: uniqueVisits([...snap.visits, ...(realDay?.unassigned ?? [])]) } : snap);
  const unassignedOfDay = (realDay?.unassigned ?? []).filter(v => v.date === date);
  // En datos reales solo aparecen carriles de quien tiene visitas ese día.
  const lanes = isReal ? snap.teams.filter(t => snap.visits.some(v => v.teamId === t.id && v.date === date)) : snap.teams;
  const isToday = date === snap.today;
  const freeCount = lanes.reduce((n, t) => n + freeGaps(snap, t.id, date).filter(g => g.end - g.start >= 2).length, 0);
  const selectedVisit = snap.visits.find(v => v.uid === selected) ?? null;

  return (
    <>
      <SourceBar />
      {isReal && !realDay ? null : <>
      <section className="grid-kpi" aria-label="Resumen del día">
        <div className="card kpi"><Ring pct={(counts.running / Math.max(counts.total, 1)) * 100} color="#2563EB" size={58} thickness={7} label={counts.running} fontSize={20} /><div><b style={{ fontSize: 15 }}>Activos ahora</b><div className="muted" style={{ fontSize: 12 }}>de {counts.total} {isToday ? 'hoy' : dayLabel(date, snap.today).toLowerCase()}</div></div></div>
        <div className="card kpi">
          <div className="ring" style={{ width: 58, height: 58, background: segments(counts) }}><span style={{ inset: 7, fontSize: 20 }}>{counts.total}</span></div>
          <div><b style={{ fontSize: 15 }}>{isToday ? 'Trabajos hoy' : `Trabajos ${dayLabel(date, snap.today).toLowerCase()}`}</b>
            <div className="row" style={{ gap: 8, fontSize: 12, fontWeight: 700, marginTop: 3 }}>
              <span>● {counts.done}</span><span style={{ color: '#2563EB' }}>● {counts.running}</span><span style={{ color: '#B7791F' }}>● {counts.pending}</span><span style={{ color: '#E5484D' }}>● {counts.risk}</span>
            </div></div>
        </div>
        <div className="card kpi" style={{ background: '#FFF7E3' }}>
          <div className="ring" style={{ width: 58, height: 58, background: 'var(--fn-gradient)' }}><span style={{ inset: 7, fontSize: 20, background: '#FFF7E3' }}>{isReal ? unassignedOfDay.length : 2}</span></div>
          <div><b style={{ fontSize: 15 }}>{isReal ? 'Sin asignar' : 'Por asignar'}</b><div className="muted" style={{ fontSize: 12 }}>{isReal ? 'sin trabajador' : 'llegaron hoy'}</div></div>
        </div>
        {!isReal && <button type="button" className="card kpi" style={{ border: 0, cursor: 'pointer', textAlign: 'left' }} onClick={() => s.setAgendaMode('estado')}>
          <Ring pct={((MONTH_TOTAL.done + MONTH_TOTAL.scheduled) / MONTH_TOTAL.projected) * 100} color="#1F1F21" size={58} thickness={7} fontSize={14} />
          <div><b style={{ fontSize: 15 }}>Octubre</b><div className="muted" style={{ fontSize: 12 }}>{MONTH_TOTAL.done + MONTH_TOTAL.scheduled} de ~{MONTH_TOTAL.projected}</div></div>
        </button>}
      </section>

      <div className="row wrap" style={{ alignItems: 'flex-start', gap: 18 }}>
        <div style={{ flex: '999 1 640px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <section className="card" aria-label="Calendario por equipo" style={{ overflow: 'hidden' }}>
            <div className="row wrap" style={{ justifyContent: 'space-between', padding: '14px 18px' }}>
              <div className="row">
                <b style={{ fontSize: 17 }}>{`${isToday ? 'Hoy' : dayLabel(date, snap.today)} por ${isReal ? 'trabajador' : 'equipo'}`}</b>
                <Chip bg="#E9F7EF" fg="#16603D"><Icon name="check" color="#16603D" size={13} />{freeCount} espacios libres</Chip>
                {!isToday && <button type="button" className="btn sm" onClick={() => setDate(snap.today)}>Volver a hoy</button>}
              </div>
              {!isReal && <div className="row" style={{ fontSize: 12 }}>
                <span className="muted">Color por</span>
                <button type="button" className={`pill${s.colorBy === 'team' ? ' on' : ''}`} onClick={() => s.setColorBy('team')}>Equipo</button>
                <button type="button" className={`pill${s.colorBy === 'service' ? ' on' : ''}`} onClick={() => s.setColorBy('service')}>Servicio</button>
              </div>}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div className="lanes">
                <div className="row" style={{ paddingBottom: 6, gap: 0 }}>
                  <div style={{ flex: '0 0 176px' }} />
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(10, minmax(0, 1fr))' }}>
                    {HOURS.map(h => <span key={h} style={{ fontSize: 12, color: '#8A8B8E', fontWeight: 600 }}>{h}</span>)}
                  </div>
                </div>
                <div style={{ position: 'relative' }}>
                  {isToday && <div className="now-line" style={{ left: `calc(176px + (100% - 176px) * ${X(snap.now) / 100})` }} aria-hidden="true" />}
                  {isReal && lanes.length === 0 && (
                    <div className="muted" style={{ padding: '18px 4px' }}>{isToday ? 'Hoy' : dayLabel(date, snap.today)} no hay visitas con trabajador asignado.</div>
                  )}
                  {lanes.map(team => {
                    const occ = occupancy(snap, team.id, date);
                    const vs = dayVisits(snap, date, team.id);
                    const blocks = snap.blocks.filter(b => b.teamId === team.id && b.date === date);
                    const gaps = freeGaps(snap, team.id, date).filter(g => g.end - g.start >= 1);
                    return (
                      <div className="lane" key={team.id}>
                        <div className="who">
                          <Ring pct={occ * 100} color={occ >= 0.9 ? '#E5484D' : team.color} size={44} thickness={5} fontSize={11} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <b style={{ fontSize: 14 }}>{team.name}</b>
                            <Avatars team={team} color={team.color} size={22} />
                          </div>
                        </div>
                        <div className="trk">
                          {blocks.map(b => (
                            <div key={b.label + b.start} className="blk block" style={{ left: `${X(b.start)}%`, width: `${X(b.end) - X(b.start)}%` }}>{b.label}</div>
                          ))}
                          {gaps.map(g => (
                            <button key={g.start} type="button" className="free" style={{ left: `${X(g.start)}%`, width: `${X(g.end) - X(g.start)}%` }}
                              title={`Libre ${fmtHour(g.start)}–${fmtHour(g.end)}`}
                              onClick={() => {
                                s.setCaller(null); s.go('espacio');
                              }}>
                              {Array.from({ length: Math.min(3, Math.floor((g.end - g.start) / 2)) }, (_, i) => <Icon key={i} name="regular" color="#16603D" size={15} />)}
                              <span>{fmtDuration(g.end - g.start)} libre</span>
                            </button>
                          ))}
                          {vs.map(v => <VisitBlock key={v.uid} v={v} snap={snap} color={colorOf(v)} onClick={() => setSelected(selected === v.uid ? null : v.uid)} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {selectedVisit && (
              <VisitPopover v={selectedVisit} snap={snap} readOnly={isReal} onClose={() => setSelected(null)}
                onReschedule={() => setResched(buildReschedule(selectedVisit, {
                  teamId: selectedVisit.teamId, date: addDays(selectedVisit.date, 1), start: selectedVisit.start, end: selectedVisit.end,
                }))} />
            )}
          </section>

          {isReal ? <RealWeek snap={snap} unassigned={realDay?.unassigned ?? []} days={realDay?.days ?? 1} selected={date} onPick={setDate} />
            : <WeekHeatmap selected={date} onPick={setDate} />}
        </div>

        <aside style={{ flex: '0 1 340px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {isReal ? <RealUnassigned visits={unassignedOfDay} snap={snap} label={isToday ? 'hoy' : dayLabel(date, snap.today).toLowerCase()} /> : <Unassigned />}
        </aside>
      </div>
      </>}

      {resched && (
        <Confirm title="Reprogramar (simulado)" confirmLabel="Entendido" onCancel={() => setResched(null)} onConfirm={() => setResched(null)}>
          <p className="muted" style={{ margin: 0 }}>Así se enviaría al servidor. Si la visita cambió desde que la viste, el servidor responde 409 y la UI muestra “La visita cambió”.</p>
          <pre className="code">{JSON.stringify(resched, null, 2)}</pre>
        </Confirm>
      )}
    </>
  );
}

function segments(c: { total: number; done: number; running: number; pending: number; risk: number }) {
  // Sin trabajos el anillo es gris (antes se pintaba rojo, como si todo estuviera en riesgo).
  if (c.total === 0) return '#F0EFEB';
  const t = c.total;
  const a = (c.done / t) * 360;
  const b = a + (c.running / t) * 360;
  const d = b + (c.pending / t) * 360;
  return `conic-gradient(#1F1F21 0 ${a}deg, #2563EB 0 ${b}deg, #FAAF3B 0 ${d}deg, #E5484D 0 360deg)`;
}

function VisitBlock({ v, snap, color, onClick }: { v: Visit; snap: Snapshot; color: string; onClick(): void }) {
  const client = snap.clients.find(c => c.uid === v.clientUid);
  const p = progressOf(v, snap.now);
  // La vista va de 8:00 a 18:00; lo que cae fuera se recorta al borde.
  const a = Math.max(DAY_START, Math.min(v.start, DAY_END - 0.25));
  const b = Math.min(DAY_END, Math.max(v.end, a + 0.25));
  const cls = v.status === 'completed' ? 'done' : v.risk ? 'risk' : '';
  return (
    <button type="button" className={`blk ${cls}`} onClick={onClick}
      style={{ left: `${X(a)}%`, width: `${X(b) - X(a)}%`, background: tint(color) }}
      aria-label={`${client?.name}, ${fmtHour(v.start)} a ${fmtHour(v.end)}`}>
      {v.status === 'inProgress' && p.real != null && <i className="fill" style={{ width: `${p.real}%`, background: color }} />}
      <span className="lbl">
        <Icon name={v.service} color={color} size={15} />
        <span>{client?.name}</span>
        {v.status === 'completed' && <Icon name="check" color={color} size={14} />}
        {v.status === 'inProgress' && p.real != null && <b style={{ fontSize: 11, color }}>{p.real}%</b>}
        {v.risk && <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#E5484D', color: '#fff', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>!</span>}
      </span>
    </button>
  );
}

function VisitPopover({ v, snap, readOnly, onClose, onReschedule }: { v: Visit; snap: Snapshot; readOnly: boolean; onClose(): void; onReschedule(): void }) {
  const s = useStore();
  const modals = useModals();
  const client = snap.clients.find(c => c.uid === v.clientUid);
  const team = snap.teams.find(t => t.id === v.teamId)!;
  const p = progressOf(v, snap.now);
  // Apoyo: equipos con hueco que se cruza con esta visita.
  const helpers = v.risk && !readOnly ? snap.teams.filter(t => t.id !== v.teamId && freeGaps(snap, t.id, v.date)
    .some(g => Math.min(g.end, v.end) - Math.max(g.start, v.start) >= Math.min(2, (v.end - v.start) / 2))) : [];
  return (
    <div className="pop" role="region" aria-label={`Detalle de ${client?.name}`}
      style={{ position: 'static', width: 'auto', margin: '0 14px 14px', borderRadius: 14 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <b>{client?.name} · {fmtHour(v.start)}–{fmtHour(v.end)}</b>
        <button type="button" onClick={onClose} aria-label="Cerrar" style={{ background: 'none', border: 0, color: '#fff', cursor: 'pointer' }}><Icon name="close" size={14} /></button>
      </div>
      <span style={{ color: '#D6D6D8' }}>{readOnly ? `${team.name}${client?.address ? ` · ${client.address}` : ''}` : `${SERVICE_LABEL[v.service as ServiceType]} · ${team.name}`}</span>
      {v.risk && <span style={{ color: '#FCA5A5' }}>{v.risk.reason}{v.risk.detail ? ` · ${v.risk.detail}` : ''}</span>}
      {p.startedAt != null && <span style={{ color: '#D6D6D8' }}>Inició {fmtHour(p.startedAt)}{p.real != null ? ` · ${p.real}% real` : ''}{p.expected != null ? ` / ${p.expected}% esperado` : ''}</span>}
      {v.status === 'scheduled' && p.startedAt == null && <span style={{ color: '#D6D6D8' }}>Llegada: se confirma al registrar inicio</span>}
      <div className="row wrap" style={{ gap: 6 }}>
        {helpers.map(h => (
          <button key={h.id} type="button" className="chip" style={{ background: '#FAAF3B', color: '#1F1F21' }}
            onClick={() => s.notify(`Apoyo sugerido: ${h.name}. Se prepara la asignación; tú la confirmas.`)}>Sumar a {h.members[0].name.split(' ')[0]}</button>
        ))}
        {v.status === 'scheduled' && !readOnly && <button type="button" className="chip" style={{ background: '#3A3A3D', color: '#fff' }} onClick={onReschedule}>Reprogramar</button>}
        <button type="button" className="chip" style={{ background: '#3A3A3D', color: '#fff' }}
          onClick={() => (v.ref && modals?.openModal
            ? modals.openModal(`Jobs-Calendar-${v.ref.jobId}-${v.ref.eventUid}`)
            : s.showSoon({ title: 'Detalle del trabajo', body: 'Abre la pantalla de trabajo que ya existe en FlowNect.' }))}>
          {v.ref ? 'Abrir visita' : 'Detalles'}</button>
      </div>
    </div>
  );
}

function WeekHeatmap({ selected, onPick }: { selected: string; onPick(d: string): void }) {
  const s = useStore();
  const snap = s.snapshot;
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(snap.today, i)), [snap.today]);
  return (
    <section className="w" aria-label="Espacio esta semana">
      <div className="wh"><h2>¿Dónde hay espacio esta semana?</h2><span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>horas libres por equipo</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))', gap: 6, alignItems: 'center' }}>
        <span />
        {days.map(d => <span key={d} style={{ fontSize: 12, fontWeight: 700, textAlign: 'center', color: d === selected ? '#1F1F21' : '#8A8B8E' }}>{dayLabel(d, snap.today)}</span>)}
        {snap.teams.map(t => (
          <Row key={t.id} label={t.name.slice(-1)} color={t.color}>
            {days.map(d => {
              const h = Math.floor(freeHours(snap, t.id, d) * 2) / 2;
              const closed = snap.closedDays.includes(d);
              const fits = Math.floor(h / 2);
              const alpha = Math.min(1, 0.25 + h / 8);
              return (
                <button key={d} type="button" className="hm" onClick={() => onPick(d)}
                  aria-label={`${t.name}, ${dayLabel(d, snap.today)}: ${closed ? 'cerrado' : h ? `${h} horas libres` : 'lleno'}`}
                  style={{ background: h ? `rgba(34,197,94,${alpha})` : '#F0EFEB', color: h ? '#0E4F31' : '#A3A4A8', outline: d === selected ? '2px solid #1F1F21' : undefined }}>
                  {closed ? 'cerrado' : h ? <>{h} h {Array.from({ length: Math.min(fits, 3) }, (_, i) => <Icon key={i} name="regular" color="#0E4F31" size={13} />)}</> : 'lleno'}
                </button>
              );
            })}
          </Row>
        ))}
      </div>
    </section>
  );
}

function Row({ label, color, children }: { label: string; color: string; children: ReactNode }) {
  return (
    <>
      <div className="row" style={{ gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: color }} /><b style={{ fontSize: 13 }}>{label}</b></div>
      {children}
    </>
  );
}

const UNASSIGNED = [
  { name: 'Casa Herrera', src: 'Llamada', when: 'hoy tarde', location: { lat: 30.249, lng: -97.757 }, durationH: 2 },
  { name: 'Oficina Nova', src: 'Web', when: 'próx. semana', location: { lat: 30.30, lng: -97.74 }, durationH: 2.5 },
];

function Unassigned() {
  const s = useStore();
  const snap = s.snapshot;
  const [done, setDone] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<null | { name: string; teamId: string; date: string; start: number; end: number }>(null);
  return (
    <section className="w">
      <div className="wh"><h3 style={{ margin: 0, fontSize: 17 }}>Por asignar</h3></div>
      {UNASSIGNED.filter(u => !done.includes(u.name)).map(u => {
        const req = { clientName: u.name, address: '', location: u.location, service: 'regular' as const, durationH: u.durationH, when: 'hoy' as const };
        const sug = u.when === 'hoy tarde' ? findSlots(snap, req, snap.today)[0] : findSlots(snap, req, addDays(snap.today, 4))[0];
        const team = sug && snap.teams.find(t => t.id === sug.teamId);
        return (
          <div key={u.name} className="row" style={{ padding: 12, borderRadius: 14, background: '#FAFAF8', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: '#FFF7E3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="regular" color="#B7791F" size={20} /></div>
            <div style={{ flex: 1, minWidth: 0 }}><b>{u.name}</b><div className="muted" style={{ fontSize: 12 }}>{u.src} · {u.when}</div></div>
            {sug && team && (
              <button type="button" className="chip" style={{ background: tint(team.color), color: team.color, fontSize: 13, padding: '6px 10px', border: 0, cursor: 'pointer' }}
                onClick={() => setConfirm({ name: u.name, teamId: sug.teamId, date: sug.date, start: sug.start, end: sug.end })}>
                {team.name.slice(-1)} · {sug.date === snap.today ? '' : `${dayLabel(sug.date, snap.today)} `}{fmtHour(sug.start)}
              </button>
            )}
          </div>
        );
      })}
      {done.length === UNASSIGNED.length && <span className="muted">Todo asignado.</span>}
      {confirm && (
        <Confirm title={`Asignar ${confirm.name}`} confirmLabel="Asignar (simulado)" onCancel={() => setConfirm(null)}
          onConfirm={() => {
            s.addVisit({ uid: `new-${Date.now()}`, date: confirm.date, teamId: confirm.teamId, clientUid: 'paola', service: 'regular',
              start: confirm.start, end: confirm.end, status: 'scheduled', assignments: [], updatedAt: new Date().toISOString() });
            setDone(d => [...d, confirm.name]);
            setConfirm(null);
            s.notify('Asignado en la vista previa. En producción se crea la visita y se prepara el aviso.');
          }}>
          <p style={{ margin: 0 }}>{snapLabel(confirm.teamId)} · {dayLabel(confirm.date, snap.today)} · {fmtHour(confirm.start)}–{fmtHour(confirm.end)}</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>No se envía ningún mensaje al cliente desde aquí.</p>
        </Confirm>
      )}
    </section>
  );
}

function snapLabel(teamId: string) { return `Equipo ${teamId}`; }

/** Selector de fuente + estado de la lectura real. */
function SourceBar() {
  const s = useStore();
  const isReal = s.dataSource === 'real';
  const r = s.real;
  const day = r.day;
  return (
    <section className="card" aria-label="Fuente de datos" style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
      <div className="row" style={{ gap: 6 }} role="radiogroup" aria-label="Datos del calendario">
        <button type="button" role="radio" aria-checked={isReal} className={`pill${isReal ? ' on' : ''}`} onClick={() => s.setDataSource('real')}>Mis visitas reales</button>
        <button type="button" role="radio" aria-checked={!isReal} className={`pill${!isReal ? ' on' : ''}`} onClick={() => s.setDataSource('demo')}>Ejemplo</button>
      </div>
      <span className="muted" style={{ fontSize: 13 }} role="status">
        {!isReal ? 'Datos de ejemplo: puedes probar ¿Hay espacio?, reprogramar y asignar (simulado).'
          : r.status === 'error' ? 'No se pudieron leer las visitas reales. Prueba “Actualizar”.'
          : !day ? 'Leyendo tus visitas de hoy…'
          : `${day.total} visitas reales de hoy a ${day.days} días · solo lectura · jornada de 8:00 a 18:00 para calcular espacios${day.outsideShift ? ` · ${day.outsideShift} fuera de ese horario` : ''}.${day.partial ? ' Atención: el servidor devolvió solo una parte de las visitas.' : ''}`}
      </span>
      {isReal && s.zoneNote && <span style={{ fontSize: 12, fontWeight: 700, color: '#B45309' }}>{s.zoneNote}</span>}
      {isReal && <button type="button" className="btn sm" style={{ marginLeft: 'auto' }} onClick={s.reloadReal} disabled={r.status === 'loading'}>
        {r.status === 'loading' ? 'Actualizando…' : 'Actualizar'}</button>}
    </section>
  );
}

function RealUnassigned({ visits, snap, label }: { visits: Visit[]; snap: Snapshot; label: string }) {
  return (
    <section className="w" aria-label="Sin asignar">
      <div className="wh"><h3 style={{ margin: 0, fontSize: 17 }}>Sin asignar {label}</h3></div>
      {visits.length === 0 && <span className="muted">Todas las visitas de {label} tienen trabajador.</span>}
      {[...visits].sort((a, b) => a.start - b.start).map(v => {
        const client = snap.clients.find(c => c.uid === v.clientUid);
        return (
          <div key={v.uid} className="row" style={{ padding: 12, borderRadius: 14, background: '#FAFAF8', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: '#FFF7E3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="alert" color="#B7791F" size={20} /></div>
            <div style={{ flex: 1, minWidth: 0 }}><b className="ellipsis">{client?.name}</b>
              <div className="muted" style={{ fontSize: 12 }}>{fmtHour(v.start)}–{fmtHour(v.end)}{client?.address ? ` · ${client.address}` : ''}</div></div>
          </div>
        );
      })}
      {visits.length > 0 && <span className="muted" style={{ fontSize: 12 }}>Asigna desde Jobs. Aquí es solo lectura por ahora.</span>}
    </section>
  );
}

/** Semana real: visitas por día y cuántas no tienen trabajador. Clic para ver ese día. */
function RealWeek({ snap, unassigned, days, selected, onPick }: {
  snap: Snapshot; unassigned: Visit[]; days: number; selected: string; onPick(d: string): void;
}) {
  const list = Array.from({ length: days }, (_, i) => addDays(snap.today, i));
  const all = uniqueVisits([...snap.visits, ...unassigned]);
  return (
    <section className="w" aria-label="Próximos días">
      <div className="wh"><h2>Próximos días</h2><span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>visitas por día</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${list.length}, minmax(0, 1fr))`, gap: 6 }}>
        {list.map(d => {
          const n = all.filter(v => v.date === d).length;
          const open = unassigned.filter(v => v.date === d).length;
          return (
            <button key={d} type="button" className="hm" onClick={() => onPick(d)} aria-pressed={d === selected}
              aria-label={`${dayLabel(d, snap.today)}: ${n} visitas${open ? `, ${open} sin asignar` : ''}`}
              style={{ flexDirection: 'column', gap: 2, height: 'auto', padding: '8px 4px', background: n ? '#EEF4FF' : '#F0EFEB', color: '#1F1F21',
                outline: d === selected ? '2px solid #1F1F21' : undefined }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#8A8B8E' }}>{dayLabel(d, snap.today)}</span>
              <b style={{ fontSize: 18 }}>{n}</b>
              {open > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#B7791F' }}>{open} sin asignar</span>}
            </button>
          );
        })}
      </div>
    </section>
  );
}
